import { InfluencerContact } from "@shared/lib/types/influencer";

import styles from "../styles.module.scss";
import {
  Avatar,
  Checkbox,
  Col,
  Divider,
  Modal,
  Row,
  Space,
  Tag,
  Typography,
} from "antd";
import { Button } from "@shared/ui/Button";
import {
  CaretDownFilled,
  CaretUpFilled,
  CheckCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";

import { useAddAgencyUser } from "../../lib/hooks/useAddAgencyUser";
import { useEditAgencyUser } from "../../lib/hooks/useEditAgencyUser";
import { Fragment, useEffect, useMemo, useState } from "react";
import { ToastService } from "@shared/lib/services";
import ContactForm from "./ContactForm";
import { AgencyUser, Contact } from "@shared/lib/types/agency";
import { ACCOUNT_STATUS } from "@shared/lib/types";
import { useGetAgencyPermissions } from "@entities/agency/lib/hooks/useGetAgencyPermissions";
import hash from "object-hash";
import { ManageAgencyUser } from "@features/ManageAgencyUsers/ManageAgencyUser/ManageAgencyUser";
import useAgencyEmployeeLoginAccess from "@entities/agency/lib/hooks/useAgencyEmployeeLoginAccess";

interface Props {
  users: AgencyUser[];
  agencyId: string;
}
export function ContactsList({ users, agencyId }: Props) {
  const { addAgencyUser, isAdded, isAdding } = useAddAgencyUser();

  const { agencyPermissions, isFetching } = useGetAgencyPermissions();

  const [reviewModalProps, setReviewModalVisible] = useState<{
    visble: boolean;
    selectedContact?: AgencyUser;
  }>({ visble: false, selectedContact: undefined });

  const handleContact = (values: AgencyUser) => {
    addAgencyUser({
      agencyId,
      agencyUser: { ...values },
    });
  };

  useEffect(() => {
    if (isAdded) {
      ToastService.success("Employees Updated");
      setReviewModalVisible({ visble: false, selectedContact: undefined });
    }
  }, [isAdded]);

  return (
    <div className={styles.card}>
      <Space
        style={{
          justifyContent: "space-between",
          width: "100%",
          alignItems: "start",
          marginBottom: "12px",
        }}
      >
        <div className={styles.permTitle}>Agency Employee</div>
        <Button
          variant="outlined"
          size="middle"
          onClick={() =>
            setReviewModalVisible({ visble: true, selectedContact: undefined })
          }
          icon={<PlusOutlined />}
          iconPosition="end"
        >
          Add Agency Employee
        </Button>
      </Space>

      <Divider style={{ margin: 0, marginBottom: 16 }} />
      {users.length ? (
        <div className={styles.permContent}>
          {users.map((permItem) => (
            <Fragment key={permItem._id}>
              <PermTile
                agencyId={agencyId}
                user={permItem}
                agencyPermissions={agencyPermissions}
                handleSelect={(user) =>
                  setReviewModalVisible({
                    visble: true,
                    selectedContact: user,
                  })
                }
              />
              <Divider style={{ margin: "0" }} />
            </Fragment>
          ))}
        </div>
      ) : (
        <div>No Employees found</div>
      )}
      <Modal
        title={
          reviewModalProps.selectedContact
            ? "Update Agency Employee"
            : "Add Agency Employee"
        }
        open={reviewModalProps.visble}
        onCancel={() =>
          setReviewModalVisible({ selectedContact: undefined, visble: false })
        }
        destroyOnClose
        footer={null}
      >
        {!reviewModalProps.selectedContact && (
          <ContactForm
            isSubmitting={isAdding}
            onSubmit={handleContact}
            initialValues={reviewModalProps.selectedContact}
            initialRoles={[]}
          />
        )}
        {reviewModalProps.selectedContact && (
          <ManageAgencyUser
            agencyId={agencyId}
            agencyUser={reviewModalProps.selectedContact}
            onSuccess={() => {
              setReviewModalVisible({
                visble: false,
                selectedContact: undefined,
              });
            }}
          />
        )}
      </Modal>
    </div>
  );
}

export function PermTile({
  user,
  handleSelect,
  agencyPermissions,
  agencyId,
}: {
  user: AgencyUser;
  handleSelect: (user: AgencyUser) => void;
  agencyPermissions: string[];
  agencyId: string;
}) {
  const [permissionsExpanded, setPermissionsExpanded] =
    useState<boolean>(false);
  const {
    enableLoginAccess,
    isEnabling,
    isEnabled,
    loginError,
    loginErrorMessage,
  } = useAgencyEmployeeLoginAccess(agencyId);

  useEffect(() => {
    if (isEnabled) {
      ToastService.success("Login access granted successfully.");
    }
  }, [isEnabled]);

  useEffect(() => {
    if (loginError) {
      ToastService.error(
        loginErrorMessage?.message ? loginErrorMessage.message : ""
      );
    }
  }, [loginError]);

  return (
    <Row style={{ width: "100%" }} justify={"space-between"}>
      <Col span={6}>
        <div className={styles.permItemTitle}>
          {user.name}
          <Button
            type="text"
            style={{ padding: "8px" }}
            onClick={() => handleSelect(user)}
          >
            <EditOutlined />
          </Button>
        </div>
        <div className={styles.permItemDesc}>{user.role}</div>
        <div className={styles.permItemDesc}>
          Agency User ID
          <Button
            type="link"
            icon={<CopyOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(user?._id || "");
              ToastService.success("Agency User ID Copied");
            }}
          ></Button>
        </div>
      </Col>
      <Col span={6}>
        <div className={styles.permItemDesc}>
          {user.countryCode} {user.phoneNumber}
        </div>
      </Col>
      <Col span={7}>
        <div className={styles.permItemDesc}>{user.emailId}</div>
      </Col>
      <Col span={5}>
        <Space wrap style={{ justifyContent: "end", width: "100%" }}>
          <Tag
            color={
              user.accountStatus === ACCOUNT_STATUS.ACTIVE ? "green" : "red"
            }
          >
            {user.accountStatus}
          </Tag>
          {user.isLoginAccessCreated === false && (
            <Button
              size="small"
              loading={isEnabling}
              iconPosition="end"
              onClick={() => {
                if (!isEnabled) {
                  enableLoginAccess({
                    agencyUserId: user._id,
                  });
                }
              }}
            >
              Enable Login Access
            </Button>
          )}
        </Space>
      </Col>
    </Row>
  );
}
